import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterOutlet, Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, NavigationSkipped } from '@angular/router';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { LanguageService } from './shared/services/language.service';
import { SseNotificationService } from './shared/services/sse-notification.service';
import { CapyStatsService } from './shared/services/capystats.service';
import { DashboardLayoutComponent } from './modules/layout';
import { AuthService } from './modules/auth/services';
import { combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

/** Minimalny odstęp między auto-reloadami po nieudanym imporcie chunka (anty-pętla). */
const CHUNK_RELOAD_COOLDOWN_MS = 30000;
const CHUNK_RELOAD_TS_KEY = 'app:chunk-reload-ts';
/** Pokazuj pasek nawigacji dopiero, gdy nawigacja trwa dłużej niż to (bez migotania). */
const NAV_INDICATOR_DELAY_MS = 150;

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet, FormsModule, CommonModule, ReactiveFormsModule, FloatLabelModule, InputTextModule, ToastModule, DashboardLayoutComponent],
})
export class AppComponent {
  title = 'frontend';
  isLoggedIn = false;
  /** Nawigacja w toku dłużej niż NAV_INDICATOR_DELAY_MS — pokaż pasek postępu. */
  readonly isNavigating = signal(false);
  private _navIndicatorTimer: ReturnType<typeof setTimeout> | null = null;
  /** Licznik trwających nawigacji — przy przeklikiwaniu Start(B) wchodzi przed
   *  Cancel(A), więc bool gubiłby pasek dla finalnej, wolnej nawigacji. */
  private _navInFlight = 0;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _sseNotification = inject(SseNotificationService);
  // Eagerly initialize CapyStats (cookieless) so the tracker injects app-wide on
  // the production panel. No consent gate (authenticated internal tool, no
  // banner); DNT/GPC are still honoured. Inert when the env key is empty.
  private readonly _capyStats = inject(CapyStatsService);

  constructor(private _languageService: LanguageService, private _authService: AuthService, private router: Router) {
    this._sseNotification.init();

    // Pasek postępu nawigacji: lazy-chunki zakładek mogą się dociągać z sieci
    // (wolne łącze mobilne) — bez wskaźnika kliknięcie wygląda jak freeze.
    this.router.events.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this._startNavIndicator();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError ||
        event instanceof NavigationSkipped
      ) {
        this._stopNavIndicator();
      }

      if (event instanceof NavigationError) {
        this._reloadOnStaleChunk(event);
      }
    });

    const isStandaloneRoute$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects.startsWith('/auth') || event.urlAfterRedirects.startsWith('/consents')),
      startWith(this.router.url.startsWith('/auth') || this.router.url.startsWith('/consents'))
    );

    combineLatest([this._authService.currentUser$, isStandaloneRoute$]).pipe(
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(
      ([currentUser, isStandaloneRoute]) => {
        this.isLoggedIn = !!currentUser && !isStandaloneRoute;
      }
    );
  }

  toggleDarkMode() {
      document.documentElement.classList.toggle('app-dark');
  }

  private _startNavIndicator(): void {
    this._navInFlight++;
    if (this._navIndicatorTimer || this.isNavigating()) {
      return; // timer/pasek już aktywny — nie restartuj (kolejna nawigacja w locie)
    }
    this._navIndicatorTimer = setTimeout(() => {
      this._navIndicatorTimer = null;
      this.isNavigating.set(true);
    }, NAV_INDICATOR_DELAY_MS);
  }

  private _stopNavIndicator(): void {
    this._navInFlight = Math.max(0, this._navInFlight - 1);
    if (this._navInFlight > 0) {
      return; // inna nawigacja wciąż trwa — zostaw pasek
    }
    if (this._navIndicatorTimer) {
      clearTimeout(this._navIndicatorTimer);
      this._navIndicatorTimer = null;
    }
    this.isNavigating.set(false);
  }

  /**
   * Po deployu stary index.html może wskazywać chunki, których już nie ma
   * (hash w nazwie) — dynamic import pada. Jednorazowy reload pobiera świeży
   * index.html; cooldown w sessionStorage chroni przed pętlą reloadów.
   */
  private _reloadOnStaleChunk(event: NavigationError): void {
    const message = String((event.error as Error)?.message ?? event.error ?? '');
    const isChunkLoadFailure =
      message.includes('Failed to fetch dynamically imported module') || // Chrome/Blink
      message.includes('Importing a module script failed') ||            // Safari/WebKit
      message.includes('error loading dynamically imported module') ||   // Firefox
      message.includes('ChunkLoadError');                                // webpack fallback

    if (!isChunkLoadFailure) {
      return;
    }

    // Ten sam komunikat pada też przy CHWILOWYM braku sieci (flaky LTE, iOS
    // freeze socketów) — reload w takim stanie dałby natywną stronę błędu
    // przeglądarki zamiast żywego SPA (brak service workera). Reload tylko gdy
    // sieć jest, czyli błąd realnie wskazuje na nieświeży chunk po deployu.
    if (navigator.onLine === false) {
      return;
    }

    try {
      const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_TS_KEY) ?? 0);
      if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
        return;
      }
      sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(Date.now()));
    } catch {
      // sessionStorage niedostępny (Safari private mode) — nie blokuj recovery,
      // ale bez cooldownu; pojedynczy reload i tak rozwiąże nieświeży chunk.
    }
    window.location.reload();
  }
}
